// src/components/RatingGraph.js
import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function RatingGraph({ lichessUsername, authToken }) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!lichessUsername) return;
    fetch(`https://lichess.org/api/user/${lichessUsername}/rating-history`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    })
      .then((r) => r.json())
      .then((hist) => {
        const byDate = {};
        hist.forEach(({ name, points }) =>
          points.forEach(([y, m, d, rating]) => {
            const key = new Date(y, m, d).toISOString().slice(0, 10);
            byDate[key] = byDate[key] || { date: key };
            byDate[key][name] = rating;
          })
        );
        setData(
          Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date))
        );
      })
      .catch(console.error);
  }, [lichessUsername, authToken]);

  // compute highest, delta, avg/day for each category
  const stats = useMemo(() => {
    const cats = ["Bullet", "Blitz", "Rapid", "Classical"];
    const out = {};
    cats.forEach((cat) => {
      const vals = data.map((d) => d[cat]).filter((v) => v != null);
      if (!vals.length) return;
      const first = vals[0],
        last = vals[vals.length - 1];
      out[cat] = {
        highest: Math.max(...vals),
        delta: last - first,
        avgDaily: ((last - first) / (vals.length - 1) || 0).toFixed(1),
      };
    });
    return out;
  }, [data]);

  return (
    <>
      {/* stats row */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          margin: "0 0 1rem 0",
          flexWrap: "wrap",
        }}
      >
        {Object.entries(stats).map(([cat, s]) => (
          <div key={cat} style={{ textAlign: "center", minWidth: 80 }}>
            <strong>{cat}</strong>
            <br />
            High: {s.highest}
            <br />
            Δ: {s.delta > 0 ? `+${s.delta}` : s.delta}
            <br />
            Avg/day: {s.avgDaily}
          </div>
        ))}
      </div>

      {/* the chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="Bullet" stroke="#8884d8" dot={false} />
          <Line type="monotone" dataKey="Blitz" stroke="#82ca9d" dot={false} />
          <Line type="monotone" dataKey="Rapid" stroke="#ffc658" dot={false} />
          <Line
            type="monotone"
            dataKey="Classical"
            stroke="#ff7300"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}
